import React, { Fragment, useState, useMemo, useEffect } from 'react';
import { Button, Card, Col, Container, Form, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min';
import { formatoMonto, resetFormatoMonto, moneyPunto, formato_rut } from '../../../common/Formatos';
import { servicePostRespuestaAumentoConciliar } from '../../../api/api-aumento-conciliar';
import { verPreInforme } from '../../../api/api-visar-provision';
import { descargarArchivo } from '../../../common/descargarArchivo';
import Mensajes from '../../mensajes';

const RECHAZO = 248003;
const RECHAZO_COBERTURA = 248007;

const Formulario = ({ showModal, setShowModal, implicado, idSolGestion, idTrxOrigen,
                      solicitud, coberturas, historial, comboRespuesta, involucrado, cfgForm, recargarDatos, onTareaGestionada }) => {

    const data_encargado = useSelector((state) => state.encargadoCompaniaReducer);
    const { llaves } = data_encargado;

    const [loading, setLoading]           = useState(false);
    const [respuesta, setRespuesta]       = useState('');
    const [mtoAutorizado, setMtoAutorizado] = useState('');
    const [comentario, setComentario]     = useState('');
    const [formErrors, setFormErrors]     = useState({});
    const [showToast, setShowToast]       = useState(false);
    const [responseData, setResponseData] = useState({});
    const [motivoRow, setMotivoRow]       = useState(null);

    const cfgLabel = (key, def) => cfgForm?.[key] || def;

    const clearError = (field) => setFormErrors(prev => { const e = { ...prev }; delete e[field]; return e; });

    const montoAutorizadoActual  = coberturas.reduce((sum, c) => sum + Number(c.montoAutoConciliar || 0), 0);
    const montoPretencionTotal   = coberturas.reduce((sum, c) => sum + Number(c.montoPretencion   || 0), 0);

    useEffect(() => {
        setMtoAutorizado(solicitud?.montoSolicitado ? formatoMonto(solicitud.montoSolicitado) : '');
        setRespuesta('');
        setComentario('');
        setFormErrors({});
    }, [solicitud]);

    useEffect(() => {
        if (!respuesta) return;
        if (Number(respuesta) === RECHAZO || Number(respuesta) === RECHAZO_COBERTURA) {
            setMtoAutorizado(formatoMonto(montoAutorizadoActual));
        } else {
            setMtoAutorizado(solicitud?.montoSolicitado ? formatoMonto(solicitud.montoSolicitado) : '');
        }
    }, [respuesta]);

    const porcAumento = useMemo(() => {
        const mtoRaw = Number(resetFormatoMonto(mtoAutorizado));
        if (!mtoRaw || !montoAutorizadoActual) return '';
        return ((mtoRaw - montoAutorizadoActual) / montoAutorizadoActual * 100).toFixed(2);
    }, [mtoAutorizado, montoAutorizadoActual]);

    const esRechazo = (Number(respuesta) === RECHAZO || Number(respuesta) === RECHAZO_COBERTURA);

    const validate = () => {
        const errors = {};
        if (!respuesta) errors.respuesta = 'Requerido';
        const mtoRaw = Number(resetFormatoMonto(mtoAutorizado));
        if (!mtoAutorizado || isNaN(mtoRaw) || mtoRaw <= 0) errors.mtoAutorizado = 'Requerido';
        else if (!esRechazo && montoPretencionTotal > 0 && mtoRaw > montoPretencionTotal) errors.mtoAutorizado = cfgLabel('atendeaumentoconciliar-error-montomayorreserva', 'El monto autorizado no puede superar la reserva vigente.');
        if (esRechazo && !comentario.trim()) errors.comentario = 'Requerido al rechazar';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        try {
            const body = {
                idCaso          : llaves?.idCaso,
                idImplicadoCaso : implicado,
                idOrganizacion  : llaves?.idOrgCaso,
                idUsuario       : llaves?.idUsuario,
                idSolGestion,
                idTrxOrigen,
                respuesta       : Number(respuesta),
                montoAutorizado : Number(resetFormatoMonto(mtoAutorizado)),
                comentario      : comentario.trim() || null
            };
            const response = await servicePostRespuestaAumentoConciliar(body);
            setResponseData(response);
            setShowToast(true);
        } catch (e) {
            setResponseData({ code: -1, message: e.message });
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseToast = () => {
        setShowToast(false);
        if (responseData?.code === 1) {
            clearForm();
            setShowModal(false);
            if (onTareaGestionada) onTareaGestionada();
        }
    };

    const clearForm = () => {
        setRespuesta('');
        setMtoAutorizado('');
        setComentario('');
        setFormErrors({});
    };

    const handleVerDocumento = async () => {
        if (!solicitud?.folioCarga) return;
        setLoading(true);
        try {
            const query = Buffer.from(JSON.stringify({
                idCaso        : String(llaves?.idCaso),
                idImplicadoCaso: String(implicado),
                idCompania    : String(llaves?.idOrgCaso),
                idfolioCarga  : solicitud.folioCarga
            })).toString('base64');
            const response = await verPreInforme(query);
            descargarArchivo(response);
        } catch (e) {
            console.log('Error al ver documento adjunto:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleVerDocumentoHistorial = async (idfolioCarga) => {
        if (!idfolioCarga) return;
        setLoading(true);
        try {
            const query = Buffer.from(JSON.stringify({
                idCaso        : String(llaves?.idCaso),
                idImplicadoCaso: String(implicado),
                idCompania    : String(llaves?.idOrgCaso),
                idfolioCarga
            })).toString('base64');
            const response = await verPreInforme(query);
            descargarArchivo(response);
        } catch (e) {
            console.log('Error al ver documento historial:', e);
        } finally {
            setLoading(false);
        }
    };

    const styleHeader     = { backgroundColor: '#4F517A', color: 'white', padding: '8px 16px', fontWeight: '600', fontSize: '14px' };
    const styleHistHeader = { backgroundColor: '#5B9BD5', color: 'white', padding: '8px 16px', fontWeight: '600', fontSize: '14px' };
    const colStyle        = { backgroundColor: '#728bb0', color: 'white' };

    const columnasHistorial = [
        {
            dataField: 'fecSolicitudFmt',
            text: cfgLabel('atendeaumentoconciliar-col-fecsolicitud', 'Fec.Solicitud'),
            headerStyle: colStyle, headerAlign: 'center', align: 'center'
        },
        {
            dataField: 'mtoSolicitado',
            text: cfgLabel('atendeaumentoconciliar-col-mtosolporcent', 'Mto.Solicitado / %'),
            headerStyle: colStyle, headerAlign: 'center', align: 'right',
            formatter: (c, row) => c ? `${formatoMonto(c)} / ${row.porcSolicitud}` : ''
        },
        {
            dataField: 'estadoSolicitud',
            text: cfgLabel('atendeaumentoconciliar-col-estado', 'Estado'),
            headerStyle: colStyle, headerAlign: 'center', align: 'center'
        },
        {
            dataField: 'mtoAutoConciliar',
            text: cfgLabel('atendeaumentoconciliar-col-mtoautoconcil', 'Mto.Auto.Conciliar'),
            headerStyle: colStyle, headerAlign: 'center', align: 'right',
            formatter: (c) => c ? formatoMonto(c) : ''
        },
        {
            dataField: 'fecRespuestaFmt',
            text: cfgLabel('atendeaumentoconciliar-col-fecrespuesta', 'Fec.Respuesta'),
            headerStyle: colStyle, headerAlign: 'center', align: 'center'
        },
        {
            dataField: 'motivos',
            isDummyField: true,
            text: cfgLabel('atendeaumentoconciliar-col-motivos', 'Motivos'),
            headerStyle: { ...colStyle, width: '80px' },
            align: 'center',
            formatter: (_, row) => (
                <div className='d-flex justify-content-center gap-1'>
                    {(row.observacionSolicitud || row.observacionRespuesta) && (
                        <OverlayTrigger overlay={<Tooltip id={`obs-${row.idSolGestion}`}>Ver motivos</Tooltip>}>
                            <Button size='sm' variant='outline-success' onClick={() => setMotivoRow(row)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-file-earmark-text" viewBox="0 0 16 16">
                                    <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z"/>
                                    <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                                </svg>
                            </Button>
                        </OverlayTrigger>
                    )}
                    {row.idfolioCarga > 0 && (
                        <OverlayTrigger overlay={<Tooltip id={`pdf-${row.idSolGestion}`}>Ver documento</Tooltip>}>
                            <Button size='sm' variant='outline-danger' onClick={() => handleVerDocumentoHistorial(row.idfolioCarga)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-file-earmark-pdf" viewBox="0 0 16 16">
                                    <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
                                    <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029z"/>
                                </svg>
                            </Button>
                        </OverlayTrigger>
                    )}
                </div>
            )
        }
    ];

    return (
        <Fragment>
            <Card>
                <Card.Header className='mt-0 mb-0' as='h6' style={{ backgroundColor: '#4F517A', color: 'white' }}>
                    {cfgLabel('atendeaumentoconciliar-title', 'Atender Solicitud Aumento a Conciliar')}
                </Card.Header>
                <Card.Body>

                    {/* Sección A: Involucrado */}
                    <div style={styleHeader}>{cfgLabel('atendeaumentoconciliar-hdr-involucrado', 'Involucrado')}</div>
                    <Container className='mb-3 border border-top-0 pt-2 pb-2'>
                        <Row>
                            <Col md='2'>
                                <Form.Label className='mb-0 mt-2' style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-lbl-rut', 'Id/Nit')}
                                </Form.Label>
                                <br />
                                <Form.Text className='mb-3'>{involucrado?.rut ? formato_rut(involucrado.rut) : '—'}</Form.Text>
                            </Col>
                            <Col md='3'>
                                <Form.Label className='mb-0 mt-2' style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-lbl-nombre', 'Nombre/Razón Social')}
                                </Form.Label>
                                <br />
                                <Form.Text className='mb-3'>{involucrado?.nombre || '—'}</Form.Text>
                            </Col>
                            <Col md='2'>
                                <Form.Label className='mb-0 mt-2' style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-lbl-tipo', 'Tipo Involucrado')}
                                </Form.Label>
                                <br />
                                <Form.Text className='mb-3'>{involucrado?.tipo || '—'}</Form.Text>
                            </Col>
                            <Col md='2'>
                                <Form.Label className='mb-0 mt-2' style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-lbl-placa', 'Placa')}
                                </Form.Label>
                                <br />
                                <Form.Text className='mb-3'>{involucrado?.patente || '—'}</Form.Text>
                            </Col>
                            <Col md='3'>
                                <Form.Label className='mb-0 mt-2' style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-lbl-lesionado', 'Implicado Lesionado')}
                                </Form.Label>
                                <br />
                                <Form.Text className='mb-3'>{involucrado?.lesionado || '—'}</Form.Text>
                            </Col>
                        </Row>
                    </Container>

                    {/* Sección B: Solicitud aumento de monto a conciliar */}
                    <div style={styleHeader}>{cfgLabel('atendeaumentoconciliar-hdr-solicitud', 'Solicitud aumento de monto a conciliar')}</div>
                    <Container className='mb-3 border border-top-0 pt-2 pb-2'>
                        <Row className='fw-bold' style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '4px', marginBottom: '4px' }}>
                            <Col md='4'>{cfgLabel('atendeaumentoconciliar-col-amparo', 'Amparo CIA')}</Col>
                            <Col md='2' className='text-end'>{cfgLabel('atendeaumentoconciliar-col-mtoautorizado', 'Monto Autorizado')}</Col>
                            <Col md='2' className='text-end'>{cfgLabel('atendeaumentoconciliar-col-mtosolicitado', 'Monto Solicitado')}</Col>
                            <Col md='2' className='text-center'>{cfgLabel('atendeaumentoconciliar-col-porcaumento', '% Aumento')}</Col>
                            <Col md='2' className='text-center'>{cfgLabel('atendeaumentoconciliar-col-fechasolicitud', 'Fecha Solicitud')}</Col>
                        </Row>
                        {coberturas.map((c, i) => (
                            <Row key={i}>
                                <Col md='4'>{c.cobertura}</Col>
                                <Col md='2' className='text-end'>{formatoMonto(c.montoAutoConciliar)}</Col>
                                <Col md='2' className='text-end'>{solicitud ? formatoMonto(solicitud.montoSolicitado) : '—'}</Col>
                                <Col md='1' className='text-center'>{solicitud?.porcSolicitud ?? '—'}</Col>
                                <Col md='3' className='text-center'>{solicitud?.fechaSolicitudFmt || '—'}</Col>
                            </Row>
                        ))}
                        <Row className='mt-3'>
                            <Col md={solicitud?.folioCarga > 0 ? '10' : '12'}>
                                <Form.Label className='mb-0' style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-lbl-comentariosol', 'Comentario Solicitante')}
                                </Form.Label>
                                <div className='p-2 border rounded' style={{ fontSize: '13px', backgroundColor: '#f8f9fa', whiteSpace: 'pre-wrap' }}>
                                    {solicitud?.comentario || '—'}
                                </div>
                            </Col>
                            {solicitud?.folioCarga > 0 && (
                                <Col md='2' className='d-flex align-items-end'>
                                    <OverlayTrigger overlay={<Tooltip>Ver documento adjunto</Tooltip>}>
                                        <Button size='sm' variant='outline-danger' onClick={handleVerDocumento}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-file-earmark-pdf" viewBox="0 0 16 16">
                                                <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
                                                <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029z"/>
                                            </svg>
                                            &nbsp;{cfgLabel('atendeaumentoconciliar-btn-doctoadjunto', 'Docto. Adjunto')}
                                        </Button>
                                    </OverlayTrigger>
                                </Col>
                            )}
                        </Row>
                    </Container>

                    {/* Sección C: Autorizar nuevo monto */}
                    <div style={styleHeader}>{cfgLabel('atendeaumentoconciliar-hdr-autorizar', 'Autorizar Nuevo Monto a Conciliar')}</div>
                    <Container className='mb-3 border border-top-0 pt-3 pb-2'>
                        <Row className='mb-2'>
                            <Col md='3'>
                                <Form.Label className='label is_required mb-1' style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-cmb-respuesta', 'Respuesta')}
                                </Form.Label>
                                <Form.Select
                                    value={respuesta}
                                    onChange={e => { setRespuesta(e.target.value); clearError('respuesta'); }}
                                    isInvalid={!!formErrors.respuesta}
                                    size='sm'
                                >
                                    <option value=''>Seleccione...</option>
                                    {comboRespuesta.map((op, i) => (
                                        <option key={i} value={op.id}>{op.nombre}</option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type='invalid'>{formErrors.respuesta}</Form.Control.Feedback>
                            </Col>
                            <Col md='2'>
                                <Form.Label className='label mb-1' style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-lbl-moneda', 'Moneda')}
                                </Form.Label>
                                <div style={{ paddingTop: '6px' }}>{solicitud?.glosaMoneda || 'COP'}</div>
                            </Col>
                            <Col md='3'>
                                <Form.Label className='label is_required mb-1' style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-lbl-autorizado', 'Autorizado a Conciliar')}
                                </Form.Label>
                                <Form.Control
                                    size='sm'
                                    value={mtoAutorizado}
                                    onChange={e => { setMtoAutorizado(e.target.value); clearError('mtoAutorizado'); }}
                                    onBlur={e => {
                                        const raw = Number(resetFormatoMonto(e.target.value));
                                        setMtoAutorizado(formatoMonto(raw));
                                        if (!esRechazo && raw > 0 && montoPretencionTotal > 0 && raw > montoPretencionTotal) {
                                            setFormErrors(prev => ({ ...prev, mtoAutorizado: cfgLabel('atendeaumentoconciliar-error-montomayorreserva', 'El monto autorizado no puede superar la reserva vigente.') }));
                                        }
                                    }}
                                    onFocus={e => setMtoAutorizado(resetFormatoMonto(e.target.value))}
                                    onKeyPress={e => moneyPunto(e, false)}
                                    placeholder='0'
                                    readOnly={esRechazo}
                                    isInvalid={!!formErrors.mtoAutorizado}
                                />
                                <Form.Control.Feedback type='invalid'>{formErrors.mtoAutorizado}</Form.Control.Feedback>
                            </Col>
                            <Col md='2'>
                                <Form.Label className='label mb-1' style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-lbl-porcaumento', '% Aumento')}
                                </Form.Label>
                                <Form.Control size='sm' value={esRechazo ? '0' : porcAumento} readOnly plaintext className='text-center border-bottom' />
                            </Col>
                        </Row>
                        <Row>
                            <Col md='12'>
                                <Form.Label className={`label mb-1 ${esRechazo ? 'is_required' : ''}`} style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    {cfgLabel('atendeaumentoconciliar-lbl-comentario', 'Comentario (se incluye en notificación respuesta)')}
                                </Form.Label>
                                <Form.Control
                                    as='textarea'
                                    rows={2}
                                    value={comentario}
                                    onChange={e => { setComentario(e.target.value); clearError('comentario'); }}
                                    isInvalid={!!formErrors.comentario}
                                />
                                <Form.Control.Feedback type='invalid'>{formErrors.comentario}</Form.Control.Feedback>
                            </Col>
                        </Row>
                    </Container>

                    {/* Sección D: Historial */}
                    <div style={styleHistHeader}>{cfgLabel('atendeaumentoconciliar-hdr-historial', 'Historial Solicitudes')}</div>
                    <ToolkitProvider bootstrap4 keyField='idSolGestion' data={historial} columns={columnasHistorial}>
                        {props => (
                            <BootstrapTable
                                {...props.baseProps}
                                bordered={false}
                                headerWrapperClasses='table-secondary'
                                pagination={paginationFactory({ sizePerPage: 5 })}
                                noDataIndication={<div className='text-center py-3'>No hay registros encontrados</div>}
                            />
                        )}
                    </ToolkitProvider>

                </Card.Body>
                <Card.Footer>
                    {showToast && (
                        <Mensajes
                            showToast={showToast}
                            handleCloseToast={handleCloseToast}
                            delay={5000}
                            responseData={responseData}
                        />
                    )}
                    <Container>
                        <Row>
                            <Col className='d-flex align-items-end justify-content-end m-2 p-0'>
                                <Button className='m-2 w-auto' variant='outline-info' onClick={() => { clearForm(); setShowModal(false); }}>
                                    {cfgLabel('atendeaumentoconciliar-btn-volver', 'Volver')}
                                </Button>
                                <Button className='m-2 w-auto' variant='danger' onClick={handleSubmit} disabled={loading}>
                                    {cfgLabel('atendeaumentoconciliar-btn-procesar', 'Procesar')}
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                </Card.Footer>
            </Card>

            {/* Modal Motivos */}
            <Modal show={!!motivoRow} onHide={() => setMotivoRow(null)} size='md' centered>
                <Modal.Header closeButton style={{ backgroundColor: '#4F517A', color: 'white' }}>
                    <Modal.Title style={{ fontSize: '15px', fontWeight: '600' }}>Motivos</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {motivoRow?.observacionSolicitud && (
                        <div className='mb-3'>
                            <div className='fw-bold mb-1' style={{ fontSize: '13px' }}>
                                {cfgLabel('atendeaumentoconciliar-lbl-comentariosol', 'Comentario Solicitante')}
                            </div>
                            <div className='p-2 border rounded' style={{ fontSize: '13px', backgroundColor: '#f8f9fa', whiteSpace: 'pre-wrap' }}>
                                {motivoRow.observacionSolicitud}
                            </div>
                        </div>
                    )}
                    {motivoRow?.observacionRespuesta && (
                        <div>
                            <div className='fw-bold mb-1' style={{ fontSize: '13px' }}>Observación CIA</div>
                            <div className='p-2 border rounded' style={{ fontSize: '13px', backgroundColor: '#f8f9fa', whiteSpace: 'pre-wrap' }}>
                                {motivoRow.observacionRespuesta}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='outline-secondary' size='sm' onClick={() => setMotivoRow(null)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>

            <style type='text/css'>{`
                .label { font-weight: bold; font-size: 14px; }
                .is_required:after { content: "*"; margin-left: 2px; color: #000; font-weight: 500; font-size: 12pt; }
            `}</style>
        </Fragment>
    );
};

export default Formulario;
